/**
 * components/pagination.tsx
 * Compact, premium page control — ‹ 1 2 3 … N › . Grayscale, matches the filter
 * chips (32px, radii.md). The current page is a filled accent chip; prev/next
 * disable at the ends (next is only clickable when a further page exists). Windows
 * to first · current±1 · last with ellipses when there are many pages.
 */
import React from 'react';
import { View, Text, Pressable } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useHover } from '../hooks/useHover';
import { web } from '../utils/web';
import { Icon } from './ui';

export interface PaginationProps {
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
}

/** first · a window around current · last, with -1 marking an ellipsis gap. */
function pageWindow(page: number, pageCount: number): number[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const out = new Set<number>([1, pageCount, page, page - 1, page + 1]);
  const sorted = [...out].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);
  const withGaps: number[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) withGaps.push(-1);
    withGaps.push(sorted[i]);
  }
  return withGaps;
}

function Arrow({ dir, disabled, onPress }: { dir: 'l' | 'r'; disabled: boolean; onPress: () => void }) {
  const { theme } = useTheme();
  const { colors, radii } = theme;
  const { hovered, hoverProps } = useHover();
  return (
    <Pressable
      {...(disabled ? {} : hoverProps)}
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      accessibilityLabel={dir === 'l' ? 'Previous page' : 'Next page'}
      style={[
        {
          width: 32, height: 32, alignItems: 'center', justifyContent: 'center',
          borderRadius: radii.md, borderWidth: 1,
          borderColor: !disabled && hovered ? colors.lineStrong : colors.line,
          backgroundColor: !disabled && hovered ? colors.paper2 : colors.card,
          opacity: disabled ? 0.4 : 1,
        },
        web({ cursor: disabled ? 'not-allowed' : 'pointer', transition: 'background 130ms ease, border-color 130ms ease' }),
      ]}
    >
      <Icon name={dir === 'l' ? 'chev-l' : 'chev-r'} size={15} color={colors.ink2} />
    </Pressable>
  );
}

function PageChip({ n, active, onPress }: { n: number; active: boolean; onPress: () => void }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { hovered, hoverProps } = useHover();
  return (
    <Pressable
      {...hoverProps}
      onPress={onPress}
      accessibilityLabel={`Page ${n}`}
      style={[
        {
          minWidth: 32, height: 32, paddingHorizontal: 6, alignItems: 'center', justifyContent: 'center',
          borderRadius: radii.md, borderWidth: 1,
          borderColor: active ? colors.accent : hovered ? colors.lineStrong : colors.line,
          backgroundColor: active ? colors.accent : hovered ? colors.paper2 : colors.card,
        },
        web({ cursor: 'pointer', transition: 'background 130ms ease, border-color 130ms ease, transform 130ms ease' }),
        !active && hovered && web({ transform: 'translateY(-1px)' }),
      ]}
    >
      <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: active ? colors.onAccent : colors.ink2 }}>
        {n}
      </Text>
    </Pressable>
  );
}

export default function Pagination({ page, pageCount, onChange }: PaginationProps) {
  const { theme } = useTheme();
  const { colors, fontSize } = theme;
  // Always render — a single "1" chip is shown by default even for one page.
  const total = Math.max(1, pageCount);
  const win = pageWindow(page, total);
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
      <Arrow dir="l" disabled={page <= 1} onPress={() => onChange(page - 1)} />
      {win.map((n, i) =>
        n === -1 ? (
          <Text key={`gap-${i}`} style={{ width: 18, textAlign: 'center', fontSize: fontSize.sm, color: colors.ink4 }}>…</Text>
        ) : (
          <PageChip key={n} n={n} active={n === page} onPress={() => onChange(n)} />
        ),
      )}
      <Arrow dir="r" disabled={page >= total} onPress={() => onChange(page + 1)} />
    </View>
  );
}
